import { useTranslation } from 'react-i18next';

import { cn } from '@/design-system';
import { formatMoney, isDecimalString, sign } from '@/shared/lib/money';

export type MoneyKind = 'amount' | 'difference' | 'adjustment' | 'clawback';

export interface MoneyValueProps {
  /** Decimal string from the API ("250000.00", "-500.00") or null when not applicable. */
  value: string | null | undefined;
  currency?: 'AED';
  /** Tables: 'never' (currency is in the column header). Standalone values: 'prefix'. */
  showCurrency?: 'never' | 'prefix';
  /**
   * amount: plain ink (negative still shows a minus sign)
   * difference: always signed (+/−), negative in red
   * adjustment / clawback: negative in red
   */
  kind?: MoneyKind;
  /** Dashboard only: 18.4M. */
  compact?: boolean;
  /** Reason shown to assistive tech (and as a tooltip) when value is null. */
  emptyReason?: string;
  emphasis?: boolean;
  className?: string;
}

/**
 * The only way to display money. Never converts to a JS number, uses tabular figures,
 * a true minus sign and stays left-to-right inside RTL layouts.
 */
export function MoneyValue({ value, currency = 'AED', showCurrency = 'never', kind = 'amount', compact = false, emptyReason, emphasis, className }: MoneyValueProps) {
  const { t } = useTranslation();
  if (value === null || value === undefined || value === '') {
    const reason = emptyReason ?? t('money.notAvailable');
    return (
      <span className={cn('tabular text-ink-muted', className)} title={emptyReason}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{reason}</span>
      </span>
    );
  }
  if (!isDecimalString(value)) {
    if (import.meta.env.DEV) console.error('MoneyValue received a non-decimal value', value);
    return <span className={cn('text-negative', className)}>{t('money.invalid')}</span>;
  }
  const s = sign(value);
  const signed = kind === 'difference';
  const text = formatMoney(value, { signDisplay: signed ? 'exceptZero' : 'auto', compact });
  const red = s < 0 && kind !== 'amount';
  return (
    <bdi
      dir="ltr"
      className={cn('tabular inline-flex items-baseline gap-1 whitespace-nowrap', red && 'text-negative', emphasis && 'font-semibold', className)}
    >
      {showCurrency === 'prefix' ? <span className="text-[0.86em] font-normal text-ink-muted">{currency}</span> : null}
      <span>{text}</span>
    </bdi>
  );
}
